import React, { useEffect, useState } from 'react';
import { api } from '../context/AuthContext';
import { ClipboardList, ChevronRight, Clock, Check, Loader2 } from 'lucide-react';

/**
 * Student-side widget showing class assignments. When clicked, calls
 * `onOpenInMark(assignment)` so the parent can pre-fill the Mark tab.
 */
export default function StudentAssignmentsCard({ onOpenInMark }) {
    const [items, setItems] = useState(null);

    useEffect(() => {
        api.get('/api/student/assignments').then((r) => setItems(r.data || [])).catch(() => setItems([]));
    }, []);

    if (items === null) return null;
    if (items.length === 0) return null; // hide entirely if no assignments

    const pending = items.filter((i) => !i.submitted);
    const completed = items.filter((i) => i.submitted);

    return (
        <div className="bg-white border-2 border-zinc-950 rounded-2xl p-5 mb-6" data-testid="student-assignments-card">
            <div className="flex items-center justify-between mb-3">
                <h3 className="font-black text-sm inline-flex items-center gap-2">
                    <ClipboardList className="w-4 h-4 text-[#7C3AED]" /> Assignments
                </h3>
                <span className="text-xs font-bold text-zinc-500">{pending.length} pending</span>
            </div>

            {pending.length === 0 ? (
                <p className="text-xs text-zinc-500">All caught up! 🎉</p>
            ) : (
                <ul className="space-y-2">
                    {pending.slice(0, 5).map((a) => (
                        <li key={a.id}>
                            <button
                                onClick={() => onOpenInMark?.(a)}
                                className="w-full text-left p-3 rounded-xl bg-[#F8F7FC] border border-zinc-200 hover:border-[#7C3AED] hover:bg-[#EDE4FF]/40 transition-colors group"
                                data-testid={`student-assignment-${a.id}`}
                            >
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-[#7C3AED]">
                                            {a.classroom_name} · {a.subject}
                                            {a.marks ? ` · ${a.marks} marks` : ''}
                                        </div>
                                        <div className="text-sm font-bold truncate">
                                            {a.title || (a.paper_year ? `Paper ${a.paper_year} ${a.series} · Q${a.question_number}` : 'Class question')}
                                        </div>
                                        {a.question_text && (
                                            <div className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{a.question_text}</div>
                                        )}
                                        {a.due_date && (
                                            <div className="text-[10px] text-amber-600 font-bold mt-1 inline-flex items-center gap-1">
                                                <Clock className="w-3 h-3" /> Due {new Date(a.due_date).toLocaleDateString()}
                                            </div>
                                        )}
                                    </div>
                                    <ChevronRight className="w-4 h-4 text-zinc-400 group-hover:text-[#7C3AED]" />
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            {completed.length > 0 && (
                <details className="mt-3">
                    <summary className="text-xs font-bold text-zinc-500 cursor-pointer">Completed ({completed.length})</summary>
                    <ul className="mt-2 space-y-1">
                        {completed.slice(0, 5).map((a) => (
                            <li key={a.id} className="flex items-center justify-between text-xs text-zinc-500 px-2 py-1">
                                <span className="truncate inline-flex items-center gap-1.5"><Check className="w-3 h-3 text-emerald-600 flex-shrink-0" /> {a.title || `Q${a.question_number || ''}`}</span>
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </div>
    );
}
